import React, { useState } from "react";

interface DocumentStatusModalProps {
  documentRequest?: any;
}

const DocumentStatusModal: React.FC<DocumentStatusModalProps> = ({
  documentRequest,
}) => {
  const [cancelReason, setCancelReason] = useState("");
  const [showCancelForm, setShowCancelForm] = useState(false);

  const handleCancelRequest = () => {
    console.log(
      "Cancelling document request:",
      documentRequest?.requestId,
      "Reason:",
      cancelReason
    );
    setShowCancelForm(false);
    setCancelReason("");
  };

  const handleDownloadDocument = () => {
    console.log("Downloading document:", documentRequest?.requestId);
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "submitted":
        return "badge-soft-info";
      case "in progress":
        return "badge-soft-warning";
      case "approved":
        return "badge-soft-success";
      case "ready for pickup":
        return "badge-soft-primary";
      case "completed":
        return "badge-soft-success";
      case "rejected":
        return "badge-soft-danger";
      case "cancelled":
        return "badge-soft-secondary";
      default:
        return "badge-soft-secondary";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case "submitted":
        return "ti-send";
      case "in progress":
        return "ti-progress";
      case "approved":
        return "ti-check-circle";
      case "ready for pickup":
        return "ti-package";
      case "completed":
        return "ti-circle-check";
      case "rejected":
        return "ti-x-circle";
      case "cancelled":
        return "ti-ban";
      default:
        return "ti-info-circle";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case "high":
        return "badge-soft-danger";
      case "medium":
        return "badge-soft-warning";
      case "low":
        return "badge-soft-success";
      default:
        return "badge-soft-secondary";
    }
  };

  const canCancelRequest = () => {
    const cancellableStatuses = ["submitted", "in progress"];
    return cancellableStatuses.includes(documentRequest?.status?.toLowerCase());
  };

  const canDownload = () => {
    return (
      documentRequest?.status?.toLowerCase() === "completed" &&
      documentRequest?.downloadUrl
    );
  };

  return (
    <div className="modal fade" id="documentStatusModal">
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">Document Status</h4>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            />
          </div>
          <div className="modal-body">
            {!documentRequest ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading document details...</p>
              </div>
            ) : (
              <>
                {/* Request Header */}
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <div>
                    <h5 className="mb-1">{documentRequest.documentType}</h5>
                    <p className="text-muted mb-0">
                      Request ID: {documentRequest.requestId}
                    </p>
                  </div>
                  <div className="text-end">
                    <span
                      className={`badge ${getStatusColor(
                        documentRequest.status
                      )} mb-2`}
                    >
                      <i
                        className={`${getStatusIcon(
                          documentRequest.status
                        )} me-1`}
                      ></i>
                      {documentRequest.status}
                    </span>
                    <br />
                    <span
                      className={`badge ${getPriorityColor(
                        documentRequest.priority
                      )}`}
                    >
                      {documentRequest.priority} Priority
                    </span>
                  </div>
                </div>

                {/* Request Details */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="card border">
                      <div className="card-header bg-light">
                        <h6 className="mb-0">
                          <i className="ti ti-file-text me-2"></i>Request
                          Information
                        </h6>
                      </div>
                      <div className="card-body">
                        <table className="table table-borderless table-sm mb-0">
                          <tbody>
                            <tr>
                              <td>
                                <strong>Student:</strong>
                              </td>
                              <td>{documentRequest.studentName}</td>
                            </tr>
                            <tr>
                              <td>
                                <strong>Student ID:</strong>
                              </td>
                              <td>{documentRequest.studentId}</td>
                            </tr>
                            <tr>
                              <td>
                                <strong>Request Date:</strong>
                              </td>
                              <td>{documentRequest.requestDate}</td>
                            </tr>
                            <tr>
                              <td>
                                <strong>Purpose:</strong>
                              </td>
                              <td>{documentRequest.purpose}</td>
                            </tr>
                            <tr>
                              <td>
                                <strong>Copies:</strong>
                              </td>
                              <td>{documentRequest.copies || 1}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card border">
                      <div className="card-header bg-light">
                        <h6 className="mb-0">
                          <i className="ti ti-calendar me-2"></i>Timeline
                          Information
                        </h6>
                      </div>
                      <div className="card-body">
                        <table className="table table-borderless table-sm mb-0">
                          <tbody>
                            <tr>
                              <td>
                                <strong>Expected Date:</strong>
                              </td>
                              <td>{documentRequest.expectedDate}</td>
                            </tr>
                            {documentRequest.approvedDate && (
                              <tr>
                                <td>
                                  <strong>Approved Date:</strong>
                                </td>
                                <td className="text-success">
                                  {documentRequest.approvedDate}
                                </td>
                              </tr>
                            )}
                            {documentRequest.completedDate && (
                              <tr>
                                <td>
                                  <strong>Completed Date:</strong>
                                </td>
                                <td className="text-success">
                                  {documentRequest.completedDate}
                                </td>
                              </tr>
                            )}
                            <tr>
                              <td>
                                <strong>Processing Fee:</strong>
                              </td>
                              <td>
                                {documentRequest.processingFee
                                  ? `$${documentRequest.processingFee}`
                                  : "Free"}
                              </td>
                            </tr>
                            {documentRequest.rushFee && (
                              <tr>
                                <td>
                                  <strong>Rush Fee:</strong>
                                </td>
                                <td className="text-warning">
                                  ${documentRequest.rushFee}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status History */}
                {documentRequest.statusHistory &&
                  documentRequest.statusHistory.length > 0 && (
                    <div className="mb-4">
                      <h6 className="mb-3">
                        <i className="ti ti-history me-2"></i>Status History
                      </h6>
                      <div className="timeline">
                        {documentRequest.statusHistory.map(
                          (history: any, index: number) => (
                            <div key={index} className="timeline-item">
                              <div className="timeline-marker">
                                <i
                                  className={`${getStatusIcon(
                                    history.status
                                  )} text-${
                                    history.status === "approved" ||
                                    history.status === "completed"
                                      ? "success"
                                      : history.status === "rejected"
                                      ? "danger"
                                      : "primary"
                                  }`}
                                ></i>
                              </div>
                              <div className="timeline-content">
                                <div className="d-flex justify-content-between align-items-start">
                                  <div>
                                    <h6 className="mb-1">{history.status}</h6>
                                    <p className="text-muted mb-0">
                                      {history.comments}
                                    </p>
                                  </div>
                                  <small className="text-muted">
                                    {history.date}
                                  </small>
                                </div>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}

                {/* Admin Comments */}
                {documentRequest.adminComments && (
                  <div className="mb-4">
                    <h6 className="mb-3">
                      <i className="ti ti-message-circle me-2"></i>Administrator
                      Comments
                    </h6>
                    <div className="alert alert-light">
                      <p className="mb-0">{documentRequest.adminComments}</p>
                    </div>
                  </div>
                )}

                {/* Rejection Reason */}
                {documentRequest.status === "Rejected" &&
                  documentRequest.rejectionReason && (
                    <div className="alert alert-danger">
                      <h6>
                        <i className="ti ti-exclamation-triangle me-2"></i>
                        Rejection Reason
                      </h6>
                      <p className="mb-0">{documentRequest.rejectionReason}</p>
                      {documentRequest.allowResubmission && (
                        <p className="mb-0 mt-2">
                          <small>
                            <i className="ti ti-info-circle me-1"></i>
                            You may resubmit this request after addressing the
                            issues mentioned above.
                          </small>
                        </p>
                      )}
                    </div>
                  )}

                {/* Ready for Pickup Notice */}
                {documentRequest.status === "Ready for Pickup" && (
                  <div className="alert alert-primary">
                    <h6>
                      <i className="ti ti-package me-2"></i>Ready for Pickup
                    </h6>
                    <p className="mb-2">
                      Your document is ready for collection at the school
                      office.
                    </p>
                    <ul className="mb-0">
                      <li>
                        <strong>Office Hours:</strong> Monday - Friday, 8:00 AM
                        - 4:00 PM
                      </li>
                      <li>
                        <strong>Location:</strong> Main Office, Ground Floor
                      </li>
                      <li>
                        <strong>Bring:</strong> Photo ID and this request
                        reference
                      </li>
                    </ul>
                  </div>
                )}

                {/* Cancel Request Form */}
                {showCancelForm && (
                  <div className="border rounded p-3 bg-light">
                    <h6 className="text-danger mb-3">
                      <i className="ti ti-alert-triangle me-2"></i>Cancel
                      Request
                    </h6>
                    <div className="mb-3">
                      <label className="form-label">
                        Reason for Cancellation
                      </label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        placeholder="Please provide a reason for cancelling this request..."
                        required
                      />
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={handleCancelRequest}
                        disabled={!cancelReason.trim()}
                      >
                        <i className="ti ti-ban me-1"></i>Confirm Cancellation
                      </button>
                      <button
                        type="button"
                        className="btn btn-light btn-sm"
                        onClick={() => setShowCancelForm(false)}
                      >
                        Keep Request
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-light me-2"
              data-bs-dismiss="modal"
            >
              Close
            </button>
            {documentRequest && canDownload() && (
              <button
                type="button"
                className="btn btn-outline-primary me-2"
                onClick={handleDownloadDocument}
              >
                <i className="ti ti-download me-2"></i>Download Document
              </button>
            )}
            {documentRequest && canCancelRequest() && !showCancelForm && (
              <button
                type="button"
                className="btn btn-outline-danger me-2"
                onClick={() => setShowCancelForm(true)}
              >
                <i className="ti ti-ban me-2"></i>Cancel Request
              </button>
            )}
            {documentRequest &&
              documentRequest.status === "Rejected" &&
              documentRequest.allowResubmission && (
                <button
                  type="button"
                  className="btn btn-primary"
                  data-bs-dismiss="modal"
                >
                  <i className="ti ti-refresh me-2"></i>Resubmit Request
                </button>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentStatusModal;
